import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { Autocomplete, Chip, Skeleton, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { find, includes, isEmpty, map, some, uniqBy } from 'lodash';
import React, { FunctionComponent } from 'react';

import { enumValuesList } from 'utils/enumValues';
import { useCurrentLabUsers } from 'utils/useCurrentLabUsers';
import { useCurrentUser } from 'utils/useCurrentUser';

interface StudyAclProps {
  presetUsers?: string[];
  lockedUsers?: string[];
  onChange: (value: string[]) => void;
  studyId?: string;
}

enum StudyAclLevel {
  Anyone = 'Anyone in the lab/organization',
  Restricted = 'Restricted',
  OnlyMe = 'Only Me',
}

type LabUserOption = {
  value: string;
  text: string;
};

const StudyAcl: FunctionComponent<React.PropsWithChildren<StudyAclProps>> = ({
  presetUsers = [],
  lockedUsers = [],
  onChange,
  studyId,
}) => {
  const currentUserProfile = useCurrentUser();
  const currentUser: LabUserOption = { value: currentUserProfile.userId, text: currentUserProfile.userName };
  const lockedUserIds = Array.from(new Set(lockedUsers.filter(Boolean)));
  const initialSelectedUsers = Array.from(
    new Set([...(isEmpty(presetUsers) ? [currentUser.value] : presetUsers), ...lockedUserIds])
  );

  const [studyAclLevel, setStudyAclLevel] = React.useState<string>(
    isEmpty(presetUsers)
      ? StudyAclLevel.Anyone
      : presetUsers.length === 1 && presetUsers[0] === currentUser.value
      ? StudyAclLevel.OnlyMe
      : StudyAclLevel.Restricted
  );
  const [selectedUsers, setSelectedUsers] = React.useState<string[]>(initialSelectedUsers);

  const { data: labUserProfiles, isLoading: isLoadingUserCredentials } = useCurrentLabUsers(studyId);

  const labUsers: LabUserOption[] = uniqBy(
    [
      ...map(labUserProfiles?.users, (user) => ({
        value: user.id,
        text: user.primaryEmail,
      })),
      currentUser,
    ],
    'value'
  );

  const validSelectedUsers = selectedUsers.filter((user) => some(labUsers, (labUser) => labUser.value === user));
  const invalidSelectedUsers = selectedUsers.filter((user) => !some(labUsers, (labUser) => labUser.value === user));
  if (!isEmpty(invalidSelectedUsers)) {
    console.warn('Invalid selected users that were removed:', invalidSelectedUsers);
  }
  const restrictedUsers = Array.from(new Set([...validSelectedUsers, ...lockedUserIds]));

  return (
    <Grid item container direction="column" spacing={2}>
      <Grid item>
        <LabelledDropdown
          size="small"
          variant="outlined"
          label="Access Level"
          helperText="Choose who can open this study: anyone in the current lab or organization, only you, or a restricted list that always includes the study administrator."
          options={map(enumValuesList(StudyAclLevel), (level) => ({ text: level, value: level }))}
          value={studyAclLevel}
          onOptionSelected={(value) => {
            setStudyAclLevel(value);
            switch (value) {
              case StudyAclLevel.Anyone:
                onChange([]);
                break;
              case StudyAclLevel.OnlyMe:
                onChange([currentUser.value]);
                break;
              case StudyAclLevel.Restricted:
                onChange(restrictedUsers);
                break;
            }
          }}
        />
      </Grid>
      {studyAclLevel === 'Restricted' && (
        <Grid item sx={{ pt: 0.5 }}>
          {isLoadingUserCredentials ? (
            <Skeleton variant="rectangular" width={'100%'} height={56} />
          ) : (
            <Autocomplete<string, true, false, false>
              fullWidth
              multiple
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                  alignItems: 'flex-start',
                  gap: 0.5,
                  py: 0.75,
                },
                '& .MuiAutocomplete-tag': {
                  m: 0.375,
                },
              }}
              options={map(labUsers, (user) => user.value)}
              id="set-study-user-permissions"
              value={restrictedUsers}
              onChange={(_event, newValue: string[]) => {
                const nextUsers = Array.from(new Set([...newValue, ...lockedUserIds]));
                setSelectedUsers(nextUsers);
                onChange(nextUsers);
              }}
              getOptionLabel={(option) => find(labUsers, (user) => user.value === option)?.text}
              renderTags={(tagValue, getTagProps) =>
                map(tagValue, (option, index) => {
                  const { key, onDelete, ...tagProps } = getTagProps({ index });
                  const isLockedUser = includes(lockedUserIds, option);

                  return (
                    <Chip
                      key={key}
                      label={find(labUsers, (user) => user.value === option)?.text}
                      icon={isLockedUser ? <AdminPanelSettingsOutlinedIcon /> : undefined}
                      onDelete={isLockedUser ? undefined : onDelete}
                      {...tagProps}
                    />
                  );
                })
              }
              renderInput={(params) => <TextField {...params} placeholder="Add People" size="small" />}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default StudyAcl;
